<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useFunctionGetter,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import actibidAPI from 'dashboard/api/integrations/actibid';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Integration from './Integration.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const store = useStore();
const { t } = useI18n();

const integrationLoaded = ref(false);
const isSaving = ref(false);
const isTesting = ref(false);
const lastTest = ref(null);

const form = ref({
  actibid_base_url: 'https://actibid.com',
  actibid_integration_key: '',
});

const integration = useFunctionGetter('integrations/getIntegration', 'actibid');
const uiFlags = useMapGetter('integrations/getUIFlags');

const hasHook = computed(() => !!integration.value?.hooks?.length);
const hookSettings = computed(
  () => integration.value?.hooks?.[0]?.settings || {}
);

const integrationAction = computed(() => {
  if (hasHook.value) return 'disconnect';
  return '';
});

const actibidUrl = computed(() => {
  return (
    lastTest.value?.intranet_url ||
    hookSettings.value.actibid_base_url ||
    'https://actibid.com'
  );
});

const extractError = error => {
  return (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    t('INTEGRATION_SETTINGS.ACTIBID.ALERTS.ERROR')
  );
};

const loadStatus = async ({ alertOnSuccess } = {}) => {
  if (!hasHook.value) {
    lastTest.value = null;
    return;
  }

  try {
    const { data } = await actibidAPI.testConnection();
    lastTest.value = data.data || null;
    if (alertOnSuccess) {
      useAlert(
        t('INTEGRATION_SETTINGS.ACTIBID.ALERTS.TEST_OK', {
          toolsCount: lastTest.value?.tools_count,
        })
      );
    }
  } catch (error) {
    lastTest.value = null;
    useAlert(extractError(error));
  }
};

const initialize = async () => {
  await store.dispatch('integrations/get');
  integrationLoaded.value = true;
  await loadStatus();
};

const handleSave = async () => {
  isSaving.value = true;
  try {
    await store.dispatch('integrations/createHook', {
      app_id: 'actibid',
      settings: { ...form.value },
    });
    useAlert(t('INTEGRATION_SETTINGS.ACTIBID.ALERTS.SAVED'));
    await store.dispatch('integrations/get');
    await loadStatus({ alertOnSuccess: true });
  } catch (error) {
    useAlert(extractError(error));
    await store.dispatch('integrations/get');
  } finally {
    isSaving.value = false;
  }
};

const handleTest = async () => {
  isTesting.value = true;
  try {
    await loadStatus({ alertOnSuccess: true });
  } finally {
    isTesting.value = false;
  }
};

const handleOpen = () => {
  if (!actibidUrl.value) {
    useAlert(t('INTEGRATION_SETTINGS.ACTIBID.ALERTS.OPEN_MISSING'));
    return;
  }
  window.open(actibidUrl.value, '_blank', 'noopener,noreferrer');
};

onMounted(initialize);
</script>

<template>
  <SettingsLayout :is-loading="!integrationLoaded">
    <template #header>
      <BaseSettingsHeader
        :title="t('INTEGRATION_SETTINGS.ACTIBID.HEADER')"
        description=""
        feature-name="integrations"
        :back-button-label="t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-6">
        <Integration
          :integration-id="integration.id || 'actibid'"
          :integration-name="integration.name"
          :integration-description="integration.description"
          :integration-enabled="hasHook"
          :integration-action="integrationAction"
          :delete-confirmation-text="{
            title: t('INTEGRATION_SETTINGS.ACTIBID.DELETE.TITLE'),
            message: t('INTEGRATION_SETTINGS.ACTIBID.DELETE.MESSAGE'),
          }"
        >
          <template #action>
            <span />
          </template>
        </Integration>

        <form
          v-if="!hasHook"
          class="flex flex-col gap-4 p-6 outline outline-n-container outline-1 bg-n-card rounded-xl"
          @submit.prevent="handleSave"
        >
          <Input
            v-model="form.actibid_base_url"
            type="url"
            :label="t('INTEGRATION_SETTINGS.ACTIBID.FORM.BASE_URL')"
            :message="t('INTEGRATION_SETTINGS.ACTIBID.FORM.BASE_URL_HELP')"
          />
          <Input
            v-model="form.actibid_integration_key"
            type="password"
            :label="t('INTEGRATION_SETTINGS.ACTIBID.FORM.API_TOKEN')"
            :message="t('INTEGRATION_SETTINGS.ACTIBID.FORM.API_TOKEN_HELP')"
          />
          <div class="flex justify-end">
            <Button
              type="submit"
              teal
              :label="t('INTEGRATION_SETTINGS.ACTIBID.FORM.SUBMIT')"
              :is-loading="isSaving || uiFlags.isCreatingHook"
            />
          </div>
        </form>

        <div
          v-else
          class="flex flex-col gap-4 p-6 outline outline-n-container outline-1 bg-n-card rounded-xl"
        >
          <h3 class="text-heading-3 text-n-slate-12">
            {{ t('INTEGRATION_SETTINGS.ACTIBID.STATUS.TITLE') }}
          </h3>
          <p class="text-body-main text-n-slate-11">
            {{
              lastTest
                ? t('INTEGRATION_SETTINGS.ACTIBID.STATUS.CONNECTED')
                : t('INTEGRATION_SETTINGS.ACTIBID.STATUS.DISCONNECTED')
            }}
          </p>
          <dl
            v-if="lastTest"
            class="grid grid-cols-1 md:grid-cols-2 gap-3 text-body-main"
          >
            <div>
              <dt class="text-n-slate-11">
                {{ t('INTEGRATION_SETTINGS.ACTIBID.STATUS.TOOLS') }}
              </dt>
              <dd class="text-n-slate-12">{{ lastTest.tools_count }}</dd>
            </div>
            <div v-if="lastTest.tools_slugs?.length">
              <dt class="text-n-slate-11">
                {{ t('INTEGRATION_SETTINGS.ACTIBID.STATUS.SLUGS') }}
              </dt>
              <dd class="text-n-slate-12">
                {{ lastTest.tools_slugs.join(', ') }}
              </dd>
            </div>
          </dl>
          <div class="flex flex-wrap gap-2">
            <Button
              faded
              blue
              :label="t('INTEGRATION_SETTINGS.ACTIBID.ACTIONS.TEST')"
              :is-loading="isTesting"
              @click="handleTest"
            />
            <Button
              :label="t('INTEGRATION_SETTINGS.ACTIBID.ACTIONS.OPEN')"
              @click="handleOpen"
            />
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
